import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  BasePortletStore,
  OtpDetails,
  PoeStateChangedCallback
} from 'ehs-react';
import { createPortletStore, OptimizedMaskable } from 'ehs-react';
import type { at } from './generated/Beans';
import { UserMenuStoreApi } from './stores/menuUser.store';
import { MenuUserList } from './components/MenuUserList';
import {
  cleanupNavigationBar,
  doHelpDocumentInitialization,
  scheduleRefreshAssertionCall
} from './utils';
import { initializeImprintLink } from './imprint';
import ReactDOM from 'react-dom';
import {
  disableOldJsViewAroundInput,
  getHpdSelect,
  getOtpPasswordInput,
  getSecondFactorSelect
} from './util/integrationWithOldJs';
import { OtpVerificationArea } from './components/OtpVerificationArea';
import { HpdSelectionArea } from './components/HpdSelectionArea';
import {
  LoginCredentialsStoreApi,
  useLoginCredentialsStore
} from './stores/loginCredentials.store';
import { TwoFactorSelectionArea } from './components/TwoFactorSelectionArea';
import { getMainLoginButton } from './util/login-response-helper';
import { LoginLoadMask } from './components/LoginLoadMask';

type HPDParentComboBean = at.sense.hooks.login.hpd.HPDParentComboBean;

type PortletConfiguration = at.sense.hooks.login.config.FrontendConfigurationBean;
type PortletStore = BasePortletStore<PortletConfiguration> & {
  /**
   * If portlet is ready.
   */
  ready: boolean;

  /**
   * Number of OTP attempts.
   */
  nrOfOtpAttempts: number;

  /**
   * Increments the number of OTP attempts and returns the new value.
   */
  incrementNrOfOtpAttempts: () => number;

  /**
   * The URL to the login servlet, which uses plain AJAX calls.
   * This is a different URL than the usual endpoint that is available in state property 'url'.
   */
  loginServletUrl: string;
};

/**
 * The Portlet store.
 *
 * It contains the portlet url, frontend configuration, orchestratable instance to send workflow events, and other state needed by everything, such as maybe a portlet-wide loadmask state.
 * This is automatically filled by the POE.
 *
 * usePortletStore is a hook for use inside react components, which will re-render whenever the state they request changes.
 * PortletStoreApi is for use in callbacks and async functions, not in the view.
 */
export const [
  usePortletStore,
  PortletStoreApi
] = createPortletStore<PortletStore>((set, get) => ({
  ready: true,
  nrOfOtpAttempts: 0,
  incrementNrOfOtpAttempts: () => {
    const newValue = get().nrOfOtpAttempts + 1;
    set({
      nrOfOtpAttempts: newValue
    });
    return newValue;
  },
  loginServletUrl: '/delegate/LoginServlet'
}));

function renderTwoFactorSelectionArea(): React.ReactPortal | undefined {
  const twoFactorSelectEl = getSecondFactorSelect();
  const parentEl =
    twoFactorSelectEl && disableOldJsViewAroundInput(twoFactorSelectEl);
  if (!parentEl) {
    return undefined;
  }
  const submitHandler = (selectedEntry: OtpDetails): void => {
    LoginCredentialsStoreApi.getState().applySelectedTwoFactorMethod(
      selectedEntry
    );
  };
  const twoFactorSelectionArea = (
    <TwoFactorSelectionArea onSubmit={submitHandler} />
  );
  return ReactDOM.createPortal(
    twoFactorSelectionArea,
    parentEl,
    'twoFactorSelectField'
  );
}

function renderOtpVerificationArea(): React.ReactPortal | undefined {
  const otpInputEl = getOtpPasswordInput();
  if (!otpInputEl) {
    return undefined;
  }
  const parentEl = disableOldJsViewAroundInput(otpInputEl);
  if (!parentEl) {
    return undefined;
  }

  const submitHandler = (
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void
  ): void => {
    LoginCredentialsStoreApi.getState().applyOtpValue(
      authenticationCode,
      onCodeMatched,
      onCodeMismatched
    );
  };

  const otpVerificationArea = <OtpVerificationArea onSubmit={submitHandler} />;
  return ReactDOM.createPortal(
    otpVerificationArea,
    parentEl,
    'otpPasswordField'
  );
}

function renderHpSelectionArea(): React.ReactPortal | undefined {
  const hpdSelectEl = getHpdSelect();
  if (!hpdSelectEl) {
    return undefined;
  }
  const parentEl = disableOldJsViewAroundInput(hpdSelectEl);
  if (!parentEl) {
    return undefined;
  }

  const submitHandler = (selectedHpdEntry: HPDParentComboBean): void => {
    LoginCredentialsStoreApi.getState().applySelectedHpdCompany(
      selectedHpdEntry
    );
  };

  const hpdSelectionArea = <HpdSelectionArea onSubmit={submitHandler} />;
  return ReactDOM.createPortal(hpdSelectionArea, parentEl, 'hpdSelectField');
}

function renderGlobalLoadMask(): React.ReactPortal | undefined {
  const loginBtn = getMainLoginButton();
  if (!loginBtn) {
    return undefined;
  }
  const loginContent = document.querySelector('#content .login-content');
  if (!loginContent) {
    return undefined;
  }
  return ReactDOM.createPortal(<LoginLoadMask />, loginContent);
}

function LoginButtonHandlerApp({
  clickHandler
}: {
  clickHandler: (target: Event) => void;
}): JSX.Element {
  useEffect(() => {
    const loginBtn = getMainLoginButton();
    if (loginBtn) {
      loginBtn.addEventListener('click', clickHandler);
      return () => {
        loginBtn.removeEventListener('click', clickHandler);
      };
    }
  }, [clickHandler]);
  return <></>;
}
/**
 * The main App component.
 *
 * It should render the MainView, possibly a portlet-wide loadmask and any global buttons.
 */
export function App(): JSX.Element | null {
  const [globalLoadMask, setGlobalLoadMask] = useState<
    React.ReactPortal | undefined
  >(undefined);
  const [otpVerificationField, setOtpVerificationField] = useState<
    React.ReactPortal | undefined
  >(undefined);
  const [hpdSelectionField, setHpdSelectionField] = useState<
    React.ReactPortal | undefined
  >(undefined);
  const [twoFactorSelectionArea, setTwoFactorSelectionArea] = useState<
    React.ReactPortal | undefined
  >(undefined);

  const [domContentLoaded, setDomContentLoaded] = useState(false);
  const [authenticate] = useLoginCredentialsStore(state => [
    state.authenticate
  ]);
  useEffect(() => {
    if (domContentLoaded) {
      setGlobalLoadMask(renderGlobalLoadMask());
      setTwoFactorSelectionArea(renderTwoFactorSelectionArea());
      setOtpVerificationField(renderOtpVerificationArea());
      setHpdSelectionField(renderHpSelectionArea());
    }
  }, [domContentLoaded]);

  useEffect(() => {
    const onDomLoaded = () => {
      setDomContentLoaded(true);
    };
    const onSamlLoginCallback = (e: Event) => {
      const companyHpdMap = (e as CustomEvent).detail.companyOIDToName;
      LoginCredentialsStoreApi.getState().configureSamlLoginCallback(
        companyHpdMap
      );
    };
    const showLoadMaskHandler = () => {
      LoginCredentialsStoreApi.getState().showLoadMask();
    };
    document.addEventListener('DOMContentLoaded', onDomLoaded);
    document.addEventListener('samlLoginCallback', onSamlLoginCallback);
    document.addEventListener('showLoadMask', showLoadMaskHandler);
    return () => {
      document.removeEventListener('showLoadMask', showLoadMaskHandler);
      document.removeEventListener('samlLoginCallback', onSamlLoginCallback);
      document.removeEventListener('DOMContentLoaded', onDomLoaded);
    };
  }, []);

  const loginButtonClickHandler = useCallback(
    (e: Event): void => {
      console.info('react login button handler triggered');
      e.preventDefault();
      authenticate(e.target);
    },
    [authenticate]
  );

  return (
    <OptimizedMaskable
      useStore={usePortletStore}
      selector={(state: PortletStore): boolean => !state.ready}
      className="login-hook-root-maskable"
    >
      <MenuUserList />
      <LoginButtonHandlerApp clickHandler={loginButtonClickHandler} />
      {globalLoadMask}
      {twoFactorSelectionArea}
      {otpVerificationField}
      {hpdSelectionField}
    </OptimizedMaskable>
  );
}

/**
 * Will be called upon state change of workflow.
 * @param event the event name
 */
export const poeStateChanged: PoeStateChangedCallback = async event => {
  if ('init' === event) {
    if (PortletStoreApi.getState().additionalParams?.signedIn) {
      UserMenuStoreApi.getState().load().then(cleanupNavigationBar);
      let { ssoRefreshTime } = PortletStoreApi.getState().configuration;

      // setup help icon:
      void doHelpDocumentInitialization();

      // delay refresh token:
      ssoRefreshTime = ssoRefreshTime ? new Date(ssoRefreshTime) : null;
      if (ssoRefreshTime) {
        console.info(
          'Got valid SAML SSO assertion refresh time',
          ssoRefreshTime
        );
        scheduleRefreshAssertionCall(ssoRefreshTime);
      } else {
        console.info(
          'No SAML SSO assertion refresh necessary for current user'
        );
      }
    }
    // do imprint handling anyway:
    initializeImprintLink();
  }
};
